import { cache } from "react";
import api from "./api";
import { AppRoutes } from "@/lib/routeResolver";

// ─── Type Definitions ────────────────────────────────────────────────

export interface OfferPackageRef {
  isPackage?: boolean;
  packageTitle?: string | null;
  packageName?: string | null;
  packageId?: string | null;
  pageURL?: string | null;
  destinationsIds?: string[] | null;
}

export interface OfferRegionRef {
  isRegion?: boolean;
  regionTitle?: string | null;
  regionName?: string | null;
  regionId?: string | null;
  pageURL?: string | null;
}

export interface OfferDestinationRef {
  isDestination?: boolean;
  destinationTitle?: string | null;
  destinationName?: string | null;
  destinationId?: string | null;
  pageURL?: string | null;
}

export interface SpecialOffer {
  id: string;
  title: string;
  subTitle?: string;
  shortDescription?: string;
  /** CMS-authored deal flag (e.g. "SAVE 10%"). Optional — often "" or absent. */
  offerBadge?: string | null;
  /** Terms & conditions rich HTML. Optional; carries its own heading. */
  terms?: string | null;
  photo?: string;
  isActive: boolean;
  order?: number;
  pageUrl?: string;
  package?: OfferPackageRef[];
  region?: OfferRegionRef[];
  destination?: OfferDestinationRef[];
  page?: {
    isPage?: boolean;
    pageTitle?: string | null;
    pageUrl?: string | null;
    newTab?: boolean;
  };
  downloadFile?: {
    download?: boolean;
    downloadTitle?: string | null;
    file?: string | null;
  };
}

interface SpecialOfferApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: SpecialOffer[];
}

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * Trimmed, non-empty text from a CMS field — otherwise `null`.
 *
 * The offer payload is typed but not validated at runtime, and optional fields
 * (`offerBadge`, `terms`) arrive as `""`, `null`, or absent depending on the
 * record. Narrowing on `typeof` keeps a non-string from ever reaching `.trim()`
 * and lets callers gate rendering with a plain `&&`.
 */
export function offerText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return value.trim() || null;
}

export type OfferRelationKind = "region" | "destination" | "package";

export interface OfferRelation {
  kind: OfferRelationKind;
  id: string;
  name: string;
  href: string;
}

/**
 * Returns a unified, sanitized list of entities related to an offer
 * (regions, destinations, packages). Each entry carries the route to navigate
 * to using the canonical AppRoutes constants. Entries with missing slug or
 * display name are skipped to keep the UI safe.
 *
 * Order: regions → destinations → packages (broad → specific).
 */
export function getOfferRelations(o: SpecialOffer | null | undefined): OfferRelation[] {
  if (!o) return [];

  const out: OfferRelation[] = [];

  for (const r of o.region ?? []) {
    const slug = r?.pageURL?.trim();
    const name = (r?.regionTitle || r?.regionName || "").trim();
    if (!slug || !name) continue;
    out.push({
      kind: "region",
      id: r.regionId || `region:${slug}`,
      name,
      href: AppRoutes.region(slug),
    });
  }

  for (const d of o.destination ?? []) {
    const slug = d?.pageURL?.trim();
    const name = (d?.destinationTitle || d?.destinationName || "").trim();
    if (!slug || !name) continue;
    out.push({
      kind: "destination",
      id: d.destinationId || `destination:${slug}`,
      name,
      href: AppRoutes.destination(slug),
    });
  }

  for (const p of o.package ?? []) {
    const slug = p?.pageURL?.trim();
    const name = (p?.packageTitle || p?.packageName || "").trim();
    if (!slug || !name) continue;
    out.push({
      kind: "package",
      id: p.packageId || `package:${slug}`,
      name,
      href: AppRoutes.package(slug),
    });
  }

  return out;
}

// ─── Data Fetching ───────────────────────────────────────────────────

/**
 * Fetch active special offers, sorted by order.
 * Cached per-request via React `cache()`.
 */
export const fetchActiveOffers = cache(
  async (): Promise<SpecialOffer[]> => {
    const res = await api.get<SpecialOfferApiResponse>(
      "website/specialoffer",
      { params: { PageNumber: 1, PageSize: 1000 } },
    );

    const items = res.data?.data ?? [];
    return items
      .filter((x) => x.isActive)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  },
);

// ─── Single Offer by Page URL ───────────────────────────────────────

export interface OfferDetail extends SpecialOffer {
  description?: string;
  createdDate?: string;
  isHomeShown?: boolean;
}

interface OfferDetailApiResponse {
  succeeded: boolean;
  data: OfferDetail[];
}

export const fetchOfferByPageUrl = cache(
  async (pageUrl: string): Promise<OfferDetail | null> => {
    const res = await api.get<OfferDetailApiResponse>(
      "website/specialoffer",
      { params: { PageUrl: pageUrl } },
    );

    const items = res.data?.data ?? [];
    return items[0] ?? null;
  },
);

/** Client-safe variant (no React `cache`). Use from "use client" components. */
export async function fetchOfferByPageUrlClient(
  pageUrl: string,
): Promise<OfferDetail | null> {
  const res = await api.get<OfferDetailApiResponse>("website/specialoffer", {
    params: { PageUrl: pageUrl },
  });
  const items = res.data?.data ?? [];
  return items[0] ?? null;
}
